// ─── src/api/gew.ts ───────────────────────────────────────────────────────────
//
// Admin-side API for the three GEW & Conference pre-registration surfaces
// (participants, partners, sponsors). All calls use `apiClient` (x-api-key)
// so they never leave the admin dashboard.

import { apiClient } from "./registration";
import type { PaginationMeta } from "../types/registration";

// ── Types shared with the backend ────────────────────────────────────────────

export interface GewParticipant {
  id: string;
  fullName: string;
  gender: string;
  ageGroup: string;
  whatsapp: string;
  email: string;
  governorate: string;
  cityArea: string;
  currentStatus: string;
  skills: string[];
  linkedInUrl?: string | null;
  isFounder: boolean;
  startupName?: string | null;
  startupIndustry?: string | null;
  startupStage?: string | null;
  startupCity?: string | null;
  createdAt: string;
}

export interface GewPartner {
  id: string;
  // Section 1
  orgNameAr: string;
  orgNameEn?: string | null;
  orgType: string;
  orgTypeOther?: string | null;
  orgCity: string;
  orgLinks?: string | null;
  hasCollaboratedBefore: boolean;
  previousCollaboration?: string | null;
  // Section 2
  coordinatorName: string;
  coordinatorRole: string;
  email: string;
  coordinatorPhone: string;
  isAuthorized: string;
  // Section 3
  participationScope: string;
  expectedCities?: string | null;
  participationDescription: string;
  // Section 4
  venueStatus: string;
  venueDescription?: string | null;
  willInvolveOthers: boolean;
  otherPartiesDetails?: string | null;
  // Section 5
  commitments: string[];
  createdAt: string;
}

export interface GewSponsor {
  id: string;
  orgName: string;
  email: string;
  sponsorshipType?: string | null;
  createdAt: string;
}

interface Paged<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface NormalisedList<T> {
  data: T[];
  meta: PaginationMeta;
}

const normalise = <T>(raw: Paged<T>): NormalisedList<T> => ({
  data: raw.data,
  meta: {
    total: raw.total,
    page: raw.page,
    limit: raw.limit,
    totalPages: Math.ceil(raw.total / raw.limit),
  },
});

// ── Stats shapes ─────────────────────────────────────────────────────────────

export interface GewParticipantStats {
  total: number;
  founders: number;
  byGovernorate: Record<string, number>;
  byGender: Record<string, number>;
  byAgeGroup: Record<string, number>;
  byCurrentStatus: Record<string, number>;
  bySkills: Record<string, number>;
  byStartupIndustry: Record<string, number>;
  byStartupStage: Record<string, number>;
}

export interface GewPartnerStats {
  total: number;
  priorCollaborators: number;
  byOrgType: Record<string, number>;
  byParticipationScope: Record<string, number>;
  byVenueStatus: Record<string, number>;
  commitmentsRequired: number;
}

export interface GewSponsorStats {
  total: number;
  bySponsorshipType: Record<string, number>;
}

// ── Params ───────────────────────────────────────────────────────────────────

export interface GewParticipantParams {
  page?: number;
  limit?: number;
  search?: string;
  governorate?: string;
  currentStatus?: string;
  isFounder?: "true" | "false" | "";
}

export interface GewSimpleParams {
  page?: number;
  limit?: number;
  search?: string;
  sponsorshipType?: string;
  orgType?: string;
}

// ── Calls ────────────────────────────────────────────────────────────────────

const clean = <T extends object>(params: T) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null),
  );

// Participants
export const getGewParticipants = async (params: GewParticipantParams): Promise<NormalisedList<GewParticipant>> => {
  const { data } = await apiClient.get<Paged<GewParticipant>>("/gew-participants", { params: clean(params) });
  return normalise(data);
};
export const getGewParticipantStats = async (): Promise<GewParticipantStats> => {
  const { data } = await apiClient.get<GewParticipantStats>("/gew-participants/stats");
  return data;
};
export const deleteGewParticipant = async (id: string): Promise<void> => {
  await apiClient.delete(`/gew-participants/${id}`);
};

// Partners
export const getGewPartners = async (params: GewSimpleParams): Promise<NormalisedList<GewPartner>> => {
  const { data } = await apiClient.get<Paged<GewPartner>>("/gew-partners", { params: clean(params) });
  return normalise(data);
};
export const getGewPartnerStats = async (): Promise<GewPartnerStats> => {
  const { data } = await apiClient.get<GewPartnerStats>("/gew-partners/stats");
  return data;
};
export const deleteGewPartner = async (id: string): Promise<void> => {
  await apiClient.delete(`/gew-partners/${id}`);
};

// Sponsors
export const getGewSponsors = async (params: GewSimpleParams): Promise<NormalisedList<GewSponsor>> => {
  const { data } = await apiClient.get<Paged<GewSponsor>>("/gew-sponsors", { params: clean(params) });
  return normalise(data);
};
export const getGewSponsorStats = async (): Promise<GewSponsorStats> => {
  const { data } = await apiClient.get<GewSponsorStats>("/gew-sponsors/stats");
  return data;
};
export const deleteGewSponsor = async (id: string): Promise<void> => {
  await apiClient.delete(`/gew-sponsors/${id}`);
};
